import { collectConversationDocumentIds } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/collectConversationDocumentIds'
import {
  type DocChatContextDocument,
  MAX_CONTEXT_DOCUMENT_IDS,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type {
  DocChatConversation,
  DocumentTab,
} from 'features/knowledgeDocumentViewer/types'
import { unscopeSearchDocumentIdForHydration } from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'

function validPage(pageNumber: number | undefined): { page_num?: number } {
  return pageNumber !== undefined &&
    Number.isInteger(pageNumber) &&
    pageNumber >= 1
    ? { page_num: pageNumber }
    : {}
}

function documentTabEntry(
  tab: DocumentTab
): { documentId: string; pageNumber?: number; title?: string } | undefined {
  const item = tab.history[tab.currentIndex]
  if (item?.type !== 'document') return undefined
  return {
    documentId: item.documentId,
    pageNumber: item.pageNumber,
    ...(tab.customLabel ? { title: tab.customLabel } : {}),
  }
}

/**
 * Documents the user is looking at or has discussed, for the agent to resolve
 * "this document". Depends only on tab/preview/conversation state so it can run
 * above the search hooks that consume it. The active document comes first so the
 * cap never drops it.
 */
export function collectDocChatContextDocuments({
  activeTabId,
  conversation,
  openTabs,
  pipelineConfigId,
  previewDocumentId,
  previewPage,
}: {
  activeTabId?: string
  conversation: DocChatConversation | undefined
  openTabs: readonly DocumentTab[]
  pipelineConfigId?: string
  previewDocumentId?: string
  previewPage?: number
}): DocChatContextDocument[] {
  const unscope = (documentId: string) =>
    unscopeSearchDocumentIdForHydration(documentId, pipelineConfigId)
  const byId = new Map<string, DocChatContextDocument>()
  const add = (document: DocChatContextDocument) => {
    if (document.document_id.length === 0) return
    if (!byId.has(document.document_id))
      byId.set(document.document_id, document)
  }

  const activeTabEntry = openTabs
    .filter((tab) => tab.id === activeTabId)
    .map(documentTabEntry)
    .find((entry) => entry !== undefined)

  if (previewDocumentId) {
    const documentId = unscope(previewDocumentId)
    const tabTitle = openTabs
      .map(documentTabEntry)
      .find((entry) => entry && unscope(entry.documentId) === documentId)?.title
    add({
      document_id: documentId,
      ...validPage(previewPage),
      ...(tabTitle ? { title: tabTitle } : {}),
      is_active: true,
    })
  } else if (activeTabEntry) {
    add({
      document_id: unscope(activeTabEntry.documentId),
      ...validPage(activeTabEntry.pageNumber),
      ...(activeTabEntry.title ? { title: activeTabEntry.title } : {}),
      is_active: true,
    })
  }

  for (const tab of openTabs) {
    const entry = documentTabEntry(tab)
    if (!entry) continue
    add({
      document_id: unscope(entry.documentId),
      ...validPage(entry.pageNumber),
      ...(entry.title ? { title: entry.title } : {}),
      is_active: false,
    })
  }
  for (const documentId of collectConversationDocumentIds(
    conversation,
    pipelineConfigId
  )) {
    add({ document_id: documentId, is_active: false })
  }

  return Array.from(byId.values()).slice(0, MAX_CONTEXT_DOCUMENT_IDS)
}
